import { Button, Descriptions, Drawer, Input, InputNumber, Select, Switch } from "@arco-design/web-react";
import { useEffect, useState } from "react";
import { X } from "lucide-react";

import {
  resourcePointSettlementUnits,
  resourcePointStatusOptions,
  resourcePointTicketResourceTypeOptions,
  resourcePointTravelerRoleOptions,
} from "../config";
import {
  emptyResourcePointSkuMutation,
  type ResourcePointMutationResult,
  type ResourcePointSkuMutationErrors,
} from "../runtime";
import { formatResourcePointDateTime } from "../service";
import type { ResourcePointSkuItem, ResourcePointSkuMutation, ResourcePointSpuItem } from "../types";
import { ResourcePointFormRow } from "./ResourcePointFormRow";

export type ResourcePointSkuDrawerState =
  | { type: "create"; spu: ResourcePointSpuItem }
  | { type: "edit"; spu: ResourcePointSpuItem; sku: ResourcePointSkuItem };

type ResourcePointSkuDrawerProps = {
  state: ResourcePointSkuDrawerState | null;
  pending: boolean;
  errorMessage: string;
  onClose: () => void;
  onAfterClose: () => void;
  onClearError: () => void;
  onCreate: (
    spuId: string,
    payload: ResourcePointSkuMutation,
  ) => Promise<ResourcePointMutationResult<ResourcePointSkuMutationErrors>>;
  onUpdate: (
    spuId: string,
    skuId: string,
    payload: ResourcePointSkuMutation,
  ) => Promise<ResourcePointMutationResult<ResourcePointSkuMutationErrors>>;
};

/** 新增或编辑考察点 SKU 的右侧抽屉。 */
export function ResourcePointSkuDrawer({
  state,
  pending,
  errorMessage,
  onClose,
  onAfterClose,
  onClearError,
  onCreate,
  onUpdate,
}: ResourcePointSkuDrawerProps) {
  const [payload, setPayload] = useState<ResourcePointSkuMutation>(emptyResourcePointSkuMutation);
  const [errors, setErrors] = useState<ResourcePointSkuMutationErrors>({});
  const editingSku = state?.type === "edit" ? state.sku : undefined;
  const ticketTypeOptions = preserveCurrentOption(
    resourcePointTicketResourceTypeOptions,
    payload.ticketResourceType,
  );
  const travelerRoleOptions = preserveCurrentOption(
    resourcePointTravelerRoleOptions,
    payload.travelerRole,
  );
  const settlementUnitOptions = preserveCurrentOption(
    resourcePointSettlementUnits.map((value) => ({ label: value, value })),
    payload.settlementUnit,
  );

  useEffect(() => {
    if (!state) return;
    setErrors({});
    onClearError();
    setPayload(state.type === "create" ? { ...emptyResourcePointSkuMutation } : skuToMutation(state.sku));
  }, [state]);

  /** 关闭弹窗并清理本地与运行时错误。 */
  function handleClose() {
    if (pending) return;
    setErrors({});
    onClearError();
    onClose();
  }

  /** 提交当前 SKU 表单并仅在成功后关闭。 */
  async function handleOk() {
    if (!state || pending) return;
    const result = state.type === "create"
      ? await onCreate(state.spu.resourcePointSpuId, payload)
      : await onUpdate(state.spu.resourcePointSpuId, state.sku.resourcePointSkuId, payload);
    if (!result.ok) {
      setErrors(result.errors ?? {});
      return;
    }
    setErrors({});
    onClose();
  }

  return (
    <Drawer
      {...({
        role: "dialog",
        "aria-label": state?.type === "edit" ? "编辑 SKU" : "新增 SKU",
        "aria-modal": "true",
      } as Record<string, string>)}
      className="resource-point-sku-drawer"
      title={state?.type === "edit" ? "编辑 SKU" : "新增 SKU"}
      visible={Boolean(state)}
      placement="right"
      width="40%"
      autoFocus
      focusLock
      unmountOnExit
      closeIcon={<Button type="text" size="mini" aria-label="关闭" icon={<X size={16} />} />}
      footer={(
        <Button className="resource-point-drawer__save" type="primary" long loading={pending} disabled={pending} onClick={handleOk}>
          {pending ? "保存中..." : "保存"}
        </Button>
      )}
      onCancel={handleClose}
      afterClose={onAfterClose}
    >
      <div className="resource-point-drawer__body">
        {state ? <div className="resource-point-drawer__parent">所属考察点：{state.spu.pointName}</div> : null}
        {errorMessage ? <div className="resource-point-error" role="alert">{errorMessage}</div> : null}
        <div className="resource-point-drawer__form">
          <ResourcePointFormRow label="SKU 名称" required error={errors.skuName}>
            <Input aria-label="SKU 名称" value={payload.skuName} onChange={(skuName) => setPayload({ ...payload, skuName })} />
          </ResourcePointFormRow>
          <ResourcePointFormRow label="票务资源类型" required error={errors.ticketResourceType}>
            <Select aria-label="票务资源类型" options={ticketTypeOptions} value={payload.ticketResourceType} onChange={(ticketResourceType) => setPayload({ ...payload, ticketResourceType })} />
          </ResourcePointFormRow>
          <ResourcePointFormRow label="旅客角色" required error={errors.travelerRole}>
            <Select aria-label="旅客角色" options={travelerRoleOptions} value={payload.travelerRole} onChange={(travelerRole) => setPayload({ ...payload, travelerRole })} />
          </ResourcePointFormRow>
          <ResourcePointFormRow label="结算单位" required error={errors.settlementUnit}>
            <Select aria-label="结算单位" options={settlementUnitOptions} value={payload.settlementUnit} onChange={(settlementUnit) => setPayload({ ...payload, settlementUnit })} />
          </ResourcePointFormRow>
          <ResourcePointFormRow label="需要预约" error={errors.reservationRequired}>
            <Switch aria-label="需要预约" checked={payload.reservationRequired} onChange={(reservationRequired) => setPayload({ ...payload, reservationRequired })} />
          </ResourcePointFormRow>
          <ResourcePointFormRow label="预约说明">
            <Input.TextArea aria-label="预约说明" autoSize={{ minRows: 3, maxRows: 7 }} value={payload.reservationInfoMarkdown ?? ""} onChange={(reservationInfoMarkdown) => setPayload({ ...payload, reservationInfoMarkdown })} />
          </ResourcePointFormRow>
          <ResourcePointFormRow label="最小团体人数" error={errors.minGroupSize}>
            <InputNumber min={0} precision={0} aria-label="最小团体人数" value={payload.minGroupSize} onChange={(minGroupSize) => setPayload({ ...payload, minGroupSize: finiteNumber(minGroupSize) })} />
          </ResourcePointFormRow>
          <ResourcePointFormRow label="最大团体人数" error={errors.maxGroupSize}>
            <InputNumber min={0} precision={0} aria-label="最大团体人数" value={payload.maxGroupSize} onChange={(maxGroupSize) => setPayload({ ...payload, maxGroupSize: finiteNumber(maxGroupSize) })} />
          </ResourcePointFormRow>
          {errors.groupSize ? <div className="resource-point-form-error" role="alert">{errors.groupSize}</div> : null}
          <ResourcePointFormRow label="停用" error={errors.isDisabled}>
            <Switch aria-label="停用" checked={payload.isDisabled} onChange={(isDisabled) => setPayload({ ...payload, isDisabled })} />
          </ResourcePointFormRow>
          <ResourcePointFormRow label="状态" error={errors.status}>
            <Select aria-label="状态" options={[...resourcePointStatusOptions]} value={payload.status} onChange={(status) => setPayload({ ...payload, status })} />
          </ResourcePointFormRow>
          <ResourcePointFormRow label="备注" error={errors.remark}>
            <Input.TextArea aria-label="备注" autoSize={{ minRows: 3, maxRows: 7 }} value={payload.remark} onChange={(remark) => setPayload({ ...payload, remark })} />
          </ResourcePointFormRow>
        </div>
        {editingSku ? (
          <Descriptions
            className="resource-point-drawer__details"
            title="只读详情"
            border
            size="small"
            column={1}
            tableLayout="fixed"
            data={skuDescriptionData(editingSku, "read-only")}
          />
        ) : null}
      </div>
    </Drawer>
  );
}

/** 将 SKU 详情转换为可编辑载荷。 */
function skuToMutation(sku: ResourcePointSkuItem): ResourcePointSkuMutation {
  return {
    skuName: sku.skuName,
    ticketResourceType: sku.ticketResourceType,
    travelerRole: sku.travelerRole,
    settlementUnit: sku.settlementUnit,
    reservationRequired: sku.reservationRequired,
    reservationInfoMarkdown: sku.reservationInfoMarkdown ?? "",
    minGroupSize: sku.minGroupSize,
    maxGroupSize: sku.maxGroupSize,
    isDisabled: sku.isDisabled,
    status: sku.status === "inactive" ? "inactive" : "active",
    remark: sku.remark,
  };
}

/** 生成编辑态只读审计与系统维护字段。 */
function skuDescriptionData(sku: ResourcePointSkuItem, scope: "read-only") {
  const data = [
    { label: "SKU 名称", value: detailValue(sku.skuName), readOnly: false },
    { label: "票务资源类型", value: optionLabel(resourcePointTicketResourceTypeOptions, sku.ticketResourceType), readOnly: false },
    { label: "旅客角色", value: optionLabel(resourcePointTravelerRoleOptions, sku.travelerRole), readOnly: false },
    {
      label: "结算单位",
      value: optionLabel(
        resourcePointSettlementUnits.map((value) => ({ value, label: value })),
        sku.settlementUnit,
      ),
      readOnly: false,
    },
    { label: "需要预约", value: sku.reservationRequired ? "是" : "否", readOnly: false },
    { label: "预约说明", value: detailValue(sku.reservationInfoMarkdown), readOnly: false },
    { label: "最小团体人数", value: detailValue(sku.minGroupSize), readOnly: false },
    { label: "最大团体人数", value: detailValue(sku.maxGroupSize), readOnly: false },
    { label: "停用", value: sku.isDisabled ? "已停用" : "未停用", readOnly: false },
    { label: "状态", value: optionLabel(resourcePointStatusOptions, sku.status), readOnly: false },
    { label: "备注", value: detailValue(sku.remark), readOnly: false },
    { label: "SKU ID", value: detailValue(sku.resourcePointSkuId), readOnly: true },
    { label: "SPU ID", value: detailValue(sku.resourcePointSpuId), readOnly: true },
    { label: "创建时间", value: formatResourcePointDateTime(sku.createdAt), readOnly: true },
    { label: "创建人", value: detailValue(sku.createdBy), readOnly: true },
    { label: "更新时间", value: formatResourcePointDateTime(sku.updatedAt), readOnly: true },
    { label: "更新人", value: detailValue(sku.updatedBy), readOnly: true },
  ];
  return data
    .filter((item) => scope === "read-only" && item.readOnly)
    .map(({ label, value }) => ({ label, value }));
}

/** 返回枚举配置的中文标签，未知或空值按详情规则显示。 */
function optionLabel(
  options: ReadonlyArray<{ value: string; label: string }>,
  value?: string,
) {
  return options.find((option) => option.value === value)?.label
    ?? (value ? `未知（${value}）` : "-");
}

/** 将详情空值统一显示为短横线，同时保留 0。 */
function detailValue(value?: string | number | null) {
  return value === undefined || value === null || value === "" ? "-" : String(value);
}

/** 将数据库中的既有未知值作为只在当前编辑上下文可选的保留项。 */
function preserveCurrentOption(
  options: ReadonlyArray<{ value: string; label: string }>,
  current: string,
) {
  if (!current || options.some((option) => option.value === current)) {
    return [...options];
  }
  return [...options, { value: current, label: `未知（${current}）` }];
}

/** 将 InputNumber 空值归一为 undefined。 */
function finiteNumber(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}
